import { env } from '@fim/config';
import { AppError } from '@fim/utils';

import {
  AiProviderError,
  postJson,
  readJson,
  resolveFetch,
  resolveTimeout,
} from '../internal/http.js';
import { parseSseStream } from '../internal/sse.js';
import type { AiProvider, ProviderOptions } from '../provider.js';
import type {
  AiFinishReason,
  AiModel,
  ChatChunk,
  ChatRequest,
  ChatResult,
  EmbedRequest,
  EmbedResponse,
  ReasoningEffort,
  ToolCall,
} from '../types.js';

/**
 * DeepSeek's HTTP API is OpenAI Chat-Completions compatible, so this provider
 * mirrors {@link ./openai.ts} closely. The two meaningful differences are:
 *
 *  1. **Thinking mode.** The v4 models ship an extended-reasoning ("thinking")
 *     mode toggled per request via a `thinking` object. It is *on by default*,
 *     so to get fast coding answers callers must explicitly disable it.
 *  2. **`reasoning_content`.** When thinking is on, the chain-of-thought is
 *     returned in a `reasoning_content` field alongside `content` (in both the
 *     non-streaming message and the streaming delta). We surface it through the
 *     canonical `ChatResult.reasoning` / `ChatChunk.reasoningDelta` fields.
 *
 * DeepSeek does not expose an embeddings endpoint; {@link embed} therefore
 * throws, matching the Anthropic provider.
 */
const DEFAULT_BASE_URL = 'https://api.deepseek.com';
export const DEEPSEEK_DEFAULT_MODEL = 'deepseek-v4-flash';
/** Higher-capability sibling for hard reasoning / large refactors. */
export const DEEPSEEK_PRO_MODEL = 'deepseek-v4-pro';
const DEFAULT_MAX_TOKENS = 8192;

const DEEPSEEK_MODELS: readonly AiModel[] = [
  {
    id: 'deepseek-v4-flash',
    provider: 'deepseek',
    contextWindow: 1_000_000,
    maxOutputTokens: 384_000,
    supportsStreaming: true,
    supportsEmbeddings: false,
  },
  {
    id: 'deepseek-v4-pro',
    provider: 'deepseek',
    contextWindow: 1_000_000,
    maxOutputTokens: 384_000,
    supportsStreaming: true,
    supportsEmbeddings: false,
  },
];

/**
 * Legacy DeepSeek aliases, deprecated by DeepSeek and retired 2026-07-24 15:59
 * UTC. They are no longer part of {@link DEEPSEEK_MODELS}; if one still reaches
 * the provider (a stale config or a caller-supplied string) it is repinned to the
 * v4 model that preserves the alias's INTENT, with a one-time deprecation warning:
 *
 *  - `deepseek-chat`     — the non-thinking chat model → {@link DEEPSEEK_DEFAULT_MODEL}
 *  - `deepseek-reasoner` — the reasoning model         → {@link DEEPSEEK_PRO_MODEL}
 *
 * (Server-side `deepseek-reasoner` currently routes to Flash, which silently
 * degrades heavy reasoning — repinning to Pro restores the original intent.)
 */
export const LEGACY_DEEPSEEK_ALIASES: Readonly<Record<string, string>> = {
  'deepseek-chat': DEEPSEEK_DEFAULT_MODEL,
  'deepseek-reasoner': DEEPSEEK_PRO_MODEL,
};

/** True when `model` is a retired legacy DeepSeek alias. */
export function isLegacyDeepSeekModel(model: string): boolean {
  return Object.prototype.hasOwnProperty.call(LEGACY_DEEPSEEK_ALIASES, model);
}

const warnedLegacyDeepSeekModels = new Set<string>();

/**
 * Map a legacy alias to its canonical v4 model, emitting a one-time Node
 * deprecation warning per distinct alias. Non-legacy ids pass through unchanged.
 */
export function repinLegacyDeepSeekModel(model: string): string {
  const canonical = LEGACY_DEEPSEEK_ALIASES[model];
  if (!canonical) return model;
  if (!warnedLegacyDeepSeekModels.has(model)) {
    warnedLegacyDeepSeekModels.add(model);
    process.emitWarning(
      `DeepSeek model '${model}' is a deprecated legacy alias (retired 2026-07-24); repinned to '${canonical}'. Update your configuration to use '${canonical}'.`,
      { type: 'DeprecationWarning', code: 'FIM_DEEPSEEK_LEGACY_MODEL' },
    );
  }
  return canonical;
}

/* -------------------------------------------------------------------------- */
/* Wire shapes                                                                */
/* -------------------------------------------------------------------------- */

interface DeepSeekToolCall {
  readonly id: string;
  readonly type?: 'function';
  readonly function: { readonly name: string; readonly arguments: string };
}

interface DeepSeekChatMessage {
  readonly role: 'system' | 'user' | 'assistant' | 'tool';
  readonly content: string | null;
  readonly tool_call_id?: string;
  readonly tool_calls?: DeepSeekToolCall[];
}

interface DeepSeekToolDef {
  readonly type: 'function';
  readonly function: {
    readonly name: string;
    readonly description?: string;
    readonly parameters: Record<string, unknown>;
  };
}

interface DeepSeekThinkingConfig {
  type: 'enabled' | 'disabled';
  reasoning_effort?: ReasoningEffort;
}

interface DeepSeekChatRequestBody {
  model: string;
  messages: DeepSeekChatMessage[];
  tools?: DeepSeekToolDef[];
  tool_choice?: 'auto' | 'none' | 'required';
  max_tokens?: number;
  temperature?: number;
  stream?: boolean;
  stream_options?: { include_usage: boolean };
  /**
   * DeepSeek structured output: `{type:'json_object'}` forces valid JSON (the
   * prompt must contain the word "json"). No json_schema/strict mode exists —
   * shape validation is the caller's job (review.md).
   */
  response_format?: { type: 'json_object' };
  thinking?: DeepSeekThinkingConfig;
  /**
   * Top-level effort, mirroring `thinking.reasoning_effort`. DeepSeek documents
   * the effort under `thinking` but its OpenAI-compatible surface also reads the
   * standard top-level `reasoning_effort`; sending both maximises the chance the
   * level is honoured regardless of which form the gateway expects.
   */
  reasoning_effort?: ReasoningEffort;
  user?: string;
}

interface DeepSeekUsage {
  readonly prompt_tokens: number;
  readonly completion_tokens: number;
  readonly total_tokens: number;
  // DeepSeek prefix-cache split (present when caching is active).
  readonly prompt_cache_hit_tokens?: number;
  readonly prompt_cache_miss_tokens?: number;
  // OpenAI-compatible breakdown; carries the chain-of-thought token count when
  // thinking is on. Present only when DeepSeek reports it.
  readonly completion_tokens_details?: { readonly reasoning_tokens?: number };
}

/** Map DeepSeek wire usage onto the SDK's AiUsage, preserving the cache split. */
function toAiUsage(u: DeepSeekUsage): {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheHitTokens?: number;
  cacheMissTokens?: number;
  reasoningTokens?: number;
} {
  // hit + miss partition prompt_tokens; if the wire sends only one, derive the
  // other so the ledger + cache-ratio are robust to partial payloads.
  const hit = u.prompt_cache_hit_tokens;
  const miss = u.prompt_cache_miss_tokens;
  return {
    inputTokens: u.prompt_tokens,
    outputTokens: u.completion_tokens,
    totalTokens: u.total_tokens,
    cacheHitTokens: hit ?? (miss !== undefined ? Math.max(0, u.prompt_tokens - miss) : undefined),
    cacheMissTokens: miss ?? (hit !== undefined ? Math.max(0, u.prompt_tokens - hit) : undefined),
    // Thinking-token count, when DeepSeek meters it (a subset of completion).
    reasoningTokens: u.completion_tokens_details?.reasoning_tokens,
  };
}

interface DeepSeekChatResponse {
  readonly model: string;
  readonly choices: ReadonlyArray<{
    readonly message: {
      readonly content: string | null;
      readonly reasoning_content?: string | null;
      readonly tool_calls?: DeepSeekToolCall[];
    };
    readonly finish_reason: string | null;
  }>;
  readonly usage?: DeepSeekUsage;
}

interface DeepSeekStreamToolCall {
  readonly index: number;
  readonly id?: string;
  readonly type?: string;
  readonly function?: { readonly name?: string; readonly arguments?: string };
}

interface DeepSeekChatStreamChunk {
  readonly model: string;
  readonly choices: ReadonlyArray<{
    readonly delta: {
      readonly content?: string | null;
      readonly reasoning_content?: string | null;
      readonly tool_calls?: DeepSeekStreamToolCall[];
    };
    readonly finish_reason: string | null;
  }>;
  readonly usage?: DeepSeekUsage | null;
}

/**
 * DeepSeek chat provider. Implements {@link AiProvider} via raw `fetch` against
 * `/chat/completions`, with SSE parsing for {@link stream}.
 */
class DeepSeekProvider implements AiProvider {
  readonly name = 'deepseek' as const;
  readonly defaultModel: string;
  readonly models = DEEPSEEK_MODELS;

  private readonly apiKey: string;
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly fetchImpl: typeof fetch;
  private readonly extraHeaders: Record<string, string>;

  constructor(options: ProviderOptions = {}) {
    const apiKey = options.apiKey ?? env.DEEPSEEK_API_KEY;
    if (!apiKey) {
      throw new AppError('DEEPSEEK_API_KEY is not configured', {
        code: 'AI_MISSING_API_KEY',
        statusCode: 500,
      });
    }
    this.apiKey = apiKey;
    this.baseUrl = stripTrailingSlash(
      options.baseUrl ?? env.DEEPSEEK_BASE_URL ?? DEFAULT_BASE_URL,
    );
    this.defaultModel =
      options.defaultModel ?? env.DEEPSEEK_MODEL ?? DEEPSEEK_DEFAULT_MODEL;
    this.timeoutMs = resolveTimeout(options);
    this.fetchImpl = resolveFetch(options);
    this.extraHeaders = { ...options.headers };
  }

  async chat(request: ChatRequest): Promise<string> {
    return (await this.chatVerbose(request)).content;
  }

  async chatVerbose(request: ChatRequest): Promise<ChatResult> {
    const response = await postJson({
      provider: this.name,
      url: `${this.baseUrl}/chat/completions`,
      headers: this.headers(),
      body: this.buildChatBody(request, false),
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
      signal: request.signal,
    });

    const data = await readJson<DeepSeekChatResponse>(response);
    const choice = data.choices[0];
    const reasoning = choice?.message.reasoning_content;
    const toolCalls = mapToolCalls(choice?.message.tool_calls);

    return {
      content: choice?.message.content ?? '',
      model: data.model,
      finishReason: mapFinishReason(choice?.finish_reason ?? null),
      ...(reasoning ? { reasoning } : {}),
      ...(toolCalls.length > 0 ? { toolCalls } : {}),
      usage: data.usage ? toAiUsage(data.usage) : undefined,
    };
  }

  async *stream(request: ChatRequest): AsyncIterable<ChatChunk> {
    const response = await postJson({
      provider: this.name,
      url: `${this.baseUrl}/chat/completions`,
      headers: this.headers(),
      body: this.buildChatBody(request, true),
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
      signal: request.signal,
    });

    if (!response.body) {
      throw new AiProviderError({
        provider: this.name,
        status: response.status,
        message: 'DeepSeek stream response had no body',
      });
    }

    let finishReason: AiFinishReason = 'unknown';
    let usage: DeepSeekUsage | undefined;
    // Accumulate streamed tool-call fragments by index (id/name arrive once,
    // arguments stream as JSON fragments to be concatenated).
    const toolAcc = new Map<number, { id?: string; name?: string; args: string }>();

    for await (const sse of parseSseStream(response.body)) {
      const payload = sse.data.trim();
      if (!payload) continue;
      if (payload === '[DONE]') break;

      const chunk = safeParse<DeepSeekChatStreamChunk>(payload);
      if (!chunk) continue;

      if (chunk.usage) usage = chunk.usage;

      const choice = chunk.choices[0];
      if (!choice) continue;

      // Chain-of-thought arrives before the visible answer; emit it on its own
      // channel so callers can render or ignore it independently.
      if (choice.delta.reasoning_content) {
        yield { delta: '', done: false, reasoningDelta: choice.delta.reasoning_content };
      }
      if (choice.delta.content) {
        yield { delta: choice.delta.content, done: false };
      }
      for (const tc of choice.delta.tool_calls ?? []) {
        const entry = toolAcc.get(tc.index) ?? { args: '' };
        if (tc.id) entry.id = tc.id;
        if (tc.function?.name) entry.name = tc.function.name;
        if (tc.function?.arguments) entry.args += tc.function.arguments;
        toolAcc.set(tc.index, entry);
        yield {
          delta: '',
          done: false,
          toolCallDelta: {
            index: tc.index,
            ...(tc.id ? { id: tc.id } : {}),
            ...(tc.function?.name ? { name: tc.function.name } : {}),
            ...(tc.function?.arguments ? { argumentsDelta: tc.function.arguments } : {}),
          },
        };
      }
      if (choice.finish_reason) {
        finishReason = mapFinishReason(choice.finish_reason);
      }
    }

    const toolCalls: ToolCall[] = [...toolAcc.entries()]
      .sort((a, b) => a[0] - b[0])
      // Synthesize a stable id if the stream never sent one (empty id would drop
      // tool_call_id on the wire and 400 DeepSeek); drop a fragment group that
      // never received a name (unusable).
      .map(([index, e]) => ({ id: e.id || `call_${index}`, name: e.name ?? '', arguments: e.args }))
      .filter((tc) => tc.name.length > 0);

    yield {
      delta: '',
      done: true,
      finishReason,
      ...(toolCalls.length > 0 ? { toolCalls } : {}),
      usage: usage ? toAiUsage(usage) : undefined,
    };
  }

  embed(_request: EmbedRequest): Promise<EmbedResponse> {
    throw new AppError(
      'DeepSeek does not expose an embeddings endpoint',
      { code: 'AI_EMBEDDINGS_UNSUPPORTED', statusCode: 400 },
    );
  }

  private headers(): Record<string, string> {
    return {
      authorization: `Bearer ${this.apiKey}`,
      ...this.extraHeaders,
    };
  }

  private buildChatBody(
    request: ChatRequest,
    stream: boolean,
  ): DeepSeekChatRequestBody {
    const messages: DeepSeekChatMessage[] = [];
    if (request.system) {
      messages.push({ role: 'system', content: request.system });
    }
    for (const message of request.messages) {
      const toolCalls = message.toolCalls ?? [];
      const hasToolCalls = toolCalls.length > 0;
      messages.push({
        // Canonical form for an assistant tool-call turn is content: null, which
        // stricter OpenAI-compatible surfaces require.
        content: hasToolCalls && message.content === '' ? null : message.content,
        role: message.role,
        ...(message.toolCallId ? { tool_call_id: message.toolCallId } : {}),
        ...(hasToolCalls
          ? {
              tool_calls: toolCalls.map((tc) => ({
                id: tc.id,
                type: 'function' as const,
                function: { name: tc.name, arguments: tc.arguments },
              })),
            }
          : {}),
      });
    }

    const body: DeepSeekChatRequestBody = {
      model: repinLegacyDeepSeekModel(request.model ?? this.defaultModel),
      messages,
      max_tokens: request.maxTokens ?? DEFAULT_MAX_TOKENS,
    };

    if (request.tools && request.tools.length > 0) {
      body.tools = request.tools.map((t) => ({
        type: 'function',
        function: {
          name: t.name,
          ...(t.description ? { description: t.description } : {}),
          parameters: t.parameters,
        },
      }));
      if (request.toolChoice) body.tool_choice = request.toolChoice;
    }

    if (request.responseFormat === 'json_object') {
      body.response_format = { type: 'json_object' };
    }

    const thinking = resolveThinking(request.reasoning);
    const thinkingEnabled = thinking?.type === 'enabled';
    if (thinking) {
      body.thinking = thinking;
      if (thinking.reasoning_effort) {
        body.reasoning_effort = thinking.reasoning_effort;
      }
    }

    // In thinking mode DeepSeek forbids sampling params (temperature / top_p /
    // presence_penalty / frequency_penalty) — they are rejected or silently
    // dropped — so temperature is forwarded ONLY when thinking is off (Blueprint
    // v2 §3.2). The other three are never added to the body in any mode.
    if (!thinkingEnabled && typeof request.temperature === 'number') {
      body.temperature = request.temperature;
    }
    if (stream) {
      body.stream = true;
      body.stream_options = { include_usage: true };
    }
    if (request.metadata?.userId) {
      body.user = request.metadata.userId;
    }

    return body;
  }
}

/**
 * Factory for the DeepSeek provider. Reads `DEEPSEEK_API_KEY` (and the optional
 * `DEEPSEEK_MODEL` / `DEEPSEEK_BASE_URL`) from `@fim/config` unless overridden
 * via {@link ProviderOptions}.
 */
export const deepseekProvider = (options?: ProviderOptions): AiProvider =>
  new DeepSeekProvider(options);

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Translate the canonical `reasoning` flag into DeepSeek's `thinking` object. */
function resolveThinking(
  reasoning: ChatRequest['reasoning'],
): DeepSeekThinkingConfig | undefined {
  if (reasoning === undefined) return undefined;
  if (reasoning === false) return { type: 'disabled' };
  if (reasoning === true) return { type: 'enabled', reasoning_effort: 'high' };
  return { type: 'enabled', reasoning_effort: reasoning.effort ?? 'high' };
}

function mapToolCalls(tcs?: DeepSeekToolCall[]): ToolCall[] {
  if (!tcs || tcs.length === 0) return [];
  return tcs.map((tc) => ({ id: tc.id, name: tc.function.name, arguments: tc.function.arguments }));
}

function mapFinishReason(reason: string | null): AiFinishReason {
  switch (reason) {
    case 'stop':
      return 'stop';
    case 'length':
      return 'length';
    case 'content_filter':
      return 'content_filter';
    case 'tool_calls':
    case 'function_call':
      return 'tool_use';
    case null:
      return 'unknown';
    default:
      return 'unknown';
  }
}

function safeParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}
